import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { LoadState } from '../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { Loading } from '../../../../../utils/loading/loading';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { AdminSuppliersList } from '../../../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { toSupplierTableRows } from './supplier-table-component.mapper';

const ACTIONS = {
  edit: { id: 1, titleEn: 'Edit Supplier', titleAr: 'تعديل المورد' },
  view: { id: 2, titleEn: 'View Supplier Details', titleAr: 'عرض تفاصيل المورد' },
  delete: { id: 3, titleEn: 'Delete Supplier', titleAr: 'حذف المورد' },
  // Tables shows id 4 for active rows and id 5 for inactive ones (via row.activationStatus).
  deactivate: { id: 4, titleEn: 'Deactivate Supplier', titleAr: 'إلغاء تفعيل المورد' },
  activate: { id: 5, titleEn: 'Activate Supplier', titleAr: 'تفعيل المورد' },
} satisfies Record<string, ActionInterface>;

@Component({
  selector: 'app-supplier-table-component',
  imports: [Tables, Loading, ErrorData],
  templateUrl: './supplier-table-component.html',
  styleUrl: './supplier-table-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierTableComponent {
  private locale = inject(LOCALE_ID);

  /** Suppliers of the current page only. */
  suppliers = input<AdminSuppliersList[]>([]);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  isFiltersApplied = input<boolean>(false);

  retry = output<void>();
  viewSupplier = output<number>();
  editSupplier = output<number>();
  deleteSupplier = output<number>();
  deactivateSupplier = output<number>();
  activateSupplier = output<number>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() =>
    toSupplierTableRows(
      this.suppliers(),
      this.isArabic(),
      this.locale,
      (this.currentPage() - 1) * this.itemsPerPage() + 1,
    ),
  );

  headerItems = [
    { key: 'index', label: '#' },
    { key: 'name', label: $localize`:@@adminOneSupplier:Supplier Name` },
    { key: 'dateCreated', label: $localize`:@@adminCategoriesSortCreatedDate:Date Created` },
    { key: 'actions', label: '', type: 'actions' },
  ];

  actions: ActionInterface[] = [ACTIONS.edit, ACTIONS.view, ACTIONS.deactivate, ACTIONS.activate, ACTIONS.delete];
  currentActions = signal<ActionInterface[]>(this.actions);

  handleAction(e: { event: Event; actionId: number; entity: { id: number } }) {
    e.event?.stopPropagation();
    switch (e.actionId) {
      case ACTIONS.edit.id:
        this.editSupplier.emit(e.entity.id);
        break;
      case ACTIONS.view.id:
        this.viewSupplier.emit(e.entity.id);
        break;
      case ACTIONS.delete.id:
        this.deleteSupplier.emit(e.entity.id);
        break;
      case ACTIONS.deactivate.id:
        this.deactivateSupplier.emit(e.entity.id);
        break;
      case ACTIONS.activate.id:
        this.activateSupplier.emit(e.entity.id);
        break;
    }
  }
}
